import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight, Layers, Copy, KeyRound, MonitorSmartphone } from "lucide-react";
import { getDeviceId } from "@/lib/device";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_POLLS = 8;

export default function PaymentStatus() {
  const location = useLocation();
  const isCancel = location.pathname.includes("cancel");
  const [state, setState] = useState(isCancel ? "cancelled" : "checking"); // checking|activating|paid|conflict|failed|cancelled
  const [licenseKey, setLicenseKey] = useState("");
  const [copied, setCopied] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [transferErr, setTransferErr] = useState("");

  useEffect(() => {
    if (isCancel) return;
    const sessionId = new URLSearchParams(location.search).get("session_id");
    if (!sessionId) {
      setState("failed");
      return;
    }
    const deviceId = getDeviceId();
    setSessionId(sessionId);
    let attempts = 0;

    const activate = async () => {
      setState("activating");
      try {
        const res = await axios.post(`${API}/license/activate`, { session_id: sessionId, device_id: deviceId });
        localStorage.setItem("pf_license_key", res.data.license_key);
        setLicenseKey(res.data.license_key);
        setState("paid");
      } catch (e) {
        if (e?.response?.status === 409) {
          setState("conflict");
        } else {
          setState("failed");
        }
      }
    };

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          activate();
          return;
        }
      } catch (e) {
        /* keep polling */
      }
      attempts += 1;
      if (attempts >= MAX_POLLS) {
        setState("failed");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [isCancel, location.search]);

  const copyKey = () => {
    navigator.clipboard?.writeText(licenseKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const transferHere = async () => {
    setTransferring(true);
    setTransferErr("");
    try {
      const res = await axios.post(`${API}/license/reset`, { session_id: sessionId, device_id: getDeviceId() });
      localStorage.setItem("pf_license_key", res.data.license_key);
      setLicenseKey(res.data.license_key);
      setState("paid");
    } catch (e) {
      setTransferErr(e?.response?.data?.detail || "Transfer failed. Please try again shortly.");
    } finally {
      setTransferring(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise-overlay flex items-center justify-center px-5">
      <div className="w-full max-w-md border border-[#262626] bg-[#0f0f0f] p-10 text-center">
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-6 h-6 bg-[#D4FF00] flex items-center justify-center">
            <Layers className="w-3.5 h-3.5 text-black" strokeWidth={2.5} />
          </div>
          <span className="font-head font-extrabold tracking-tighter">PrintFade Studio</span>
        </div>

        {(state === "checking" || state === "activating") && (
          <div data-testid="payment-checking">
            <Loader2 className="w-12 h-12 text-[#D4FF00] animate-spin mx-auto mb-5" />
            <h1 className="font-head font-bold text-2xl tracking-tight">
              {state === "activating" ? "Activating your license…" : "Confirming your payment…"}
            </h1>
            <p className="text-[#A3A3A3] text-sm mt-2">Hang tight, this only takes a moment.</p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="payment-success">
            <CheckCircle2 className="w-14 h-14 text-[#D4FF00] mx-auto mb-5" strokeWidth={1.8} />
            <h1 className="font-head font-extrabold text-2xl tracking-tight">You're all set!</h1>
            <p className="text-[#A3A3A3] text-sm mt-2">
              Lifetime access unlocked and locked to this device.
            </p>

            <div className="mt-6 border border-[#262626] bg-[#0A0A0A] p-4 text-left" data-testid="license-key-box">
              <div className="flex items-center gap-2 mb-2">
                <KeyRound className="w-3.5 h-3.5 text-[#D4FF00]" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3]">Your License Key</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <code className="font-mono text-[#D4FF00] text-sm break-all" data-testid="license-key-value">{licenseKey}</code>
                <button onClick={copyKey} data-testid="copy-key-btn" className="text-[#A3A3A3] hover:text-white transition-colors shrink-0">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              {copied && <p className="font-mono text-[10px] text-[#D4FF00] mt-1">Copied!</p>}
              <div className="flex items-center gap-1.5 mt-3 font-mono text-[9px] uppercase tracking-widest text-[#666]">
                <MonitorSmartphone className="w-3 h-3" /> Locked to this device · Save this key
              </div>
            </div>

            <Link
              to="/studio"
              data-testid="launch-studio-btn"
              className="mt-6 inline-flex items-center justify-center gap-3 w-full bg-[#D4FF00] text-black font-bold px-6 py-3.5 hover:bg-white transition-colors"
            >
              Launch PrintFade Studio <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {state === "conflict" && (
          <div data-testid="payment-conflict">
            <XCircle className="w-14 h-14 text-[#FF3B30] mx-auto mb-5" strokeWidth={1.8} />
            <h1 className="font-head font-bold text-2xl tracking-tight">Already active elsewhere</h1>
            <p className="text-[#A3A3A3] text-sm mt-2">
              This purchase is already activated on another device. Each license is locked to a single machine to prevent sharing.
            </p>
            <button
              onClick={transferHere}
              disabled={transferring}
              data-testid="transfer-here-btn"
              className="mt-6 inline-flex items-center justify-center gap-2 w-full bg-[#D4FF00] text-black font-bold px-6 py-3.5 hover:bg-white transition-colors disabled:opacity-60"
            >
              {transferring ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Move License to This Device
            </button>
            {transferErr && <p className="mt-3 font-mono text-[11px] text-[#FF3B30]" data-testid="transfer-error">{transferErr}</p>}
            <Link
              to="/"
              className="mt-3 inline-flex items-center justify-center gap-2 w-full border border-[#333] text-white font-semibold px-6 py-3.5 hover:bg-white hover:text-black transition-colors"
            >
              Back to Home
            </Link>
          </div>
        )}

        {state === "cancelled" && (
          <div data-testid="payment-cancelled">
            <XCircle className="w-14 h-14 text-[#A3A3A3] mx-auto mb-5" strokeWidth={1.8} />
            <h1 className="font-head font-bold text-2xl tracking-tight">Checkout cancelled</h1>
            <p className="text-[#A3A3A3] text-sm mt-2">No charge was made. You can grab lifetime access anytime.</p>
            <Link
              to="/"
              className="mt-7 inline-flex items-center justify-center gap-2 w-full border border-[#333] text-white font-semibold px-6 py-3.5 hover:bg-white hover:text-black transition-colors"
            >
              Back to Home
            </Link>
          </div>
        )}

        {state === "failed" && (
          <div data-testid="payment-failed">
            <XCircle className="w-14 h-14 text-[#FF3B30] mx-auto mb-5" strokeWidth={1.8} />
            <h1 className="font-head font-bold text-2xl tracking-tight">We couldn't confirm payment</h1>
            <p className="text-[#A3A3A3] text-sm mt-2">
              If you were charged, it will process shortly. Otherwise, please try again.
            </p>
            <Link
              to="/"
              className="mt-7 inline-flex items-center justify-center gap-2 w-full border border-[#333] text-white font-semibold px-6 py-3.5 hover:bg-white hover:text-black transition-colors"
            >
              Back to Home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
